import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'services/http/http.service';
import { ShowIfHttpError } from '@certemy/common';
import { map } from 'rxjs/operators';
import {
  OrganizationRoleCredentialPresentation,
  OrganizationRoleCredentialsPresentation,
  OrganizationRoleRow,
  OrganizationRoleUpdatePayload,
} from 'features/organization-settings/organization-roles/organization-roles.constants';
import { CasbinService } from 'services/casbin/casbin.service';
import { PolicyPermissionsService } from 'services/policy-permission/policy-permissions.service';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';

export const organizationRolesResolver: ResolveFn<Observable<OrganizationRoleRow[]>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationRolesService).getOrganizationRoles();
};

@Injectable()
export class OrganizationRolesEditPermissionResolverService  {
  constructor(private casbinService: CasbinService, private policyPermissionsService: PolicyPermissionsService) {}

  resolve(): Observable<boolean> | boolean {
    if (this.casbinService.isPermissionEnabled()) {
      return this.policyPermissionsService
        .hasPermissions('settings/organizations/organization_roles', 'edit')
        .pipe(map(({ edit }) => edit));
    }
    return false;
  }
}

export const organizationRolesEditPermissionResolver: ResolveFn<Observable<boolean> | boolean> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationRolesEditPermissionResolverService).resolve();
};

@Injectable()
export class OrganizationRolesService {
  constructor(private http: HttpService) {}

  @ShowIfHttpError('Cannot receive organization roles.')
  getOrganizationRoles(): Observable<OrganizationRoleRow[]> {
    return this.http.get('/organization/roles').pipe(map(data => data.roles));
  }

  @ShowIfHttpError('Cannot create organization role.')
  createOrganizationRole(payload: OrganizationRoleUpdatePayload): Observable<any> {
    return this.http.post('/organization/role', payload);
  }

  @ShowIfHttpError('Cannot update/delete organization role.')
  updateOrganizationRole(id: number, payload: OrganizationRoleUpdatePayload): Observable<any> {
    return this.http.put(`/organization/role/${id}`, payload);
  }

  @ShowIfHttpError('Cannot receive active credentials for the role.')
  getActiveCredentials(id: number): Observable<OrganizationRoleCredentialPresentation[]> {
    return this.http
      .get(`/organization/role/${id}/role-credential`)
      .pipe(map((response: OrganizationRoleCredentialsPresentation) => response.credentials));
  }
}
